import { useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '@services/user';
import { UserOrganizationRoleStatus } from '@models/user.model';
import { toast } from 'react-toastify';
import { isNoSeatsAvailableError } from '@utils/errorMessage';
import { useTranslation } from '@hooks/useTranslation';

interface UpdateStatusParams {
  userId: string;
  status: UserOrganizationRoleStatus;
  organizationId?: string;
}

export const useUpdateUserOrganizationStatus = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['users', 'updateOrganizationStatus'],
    mutationFn: async ({ userId, status, organizationId }: UpdateStatusParams) => {
      return await userService.updateUserOrganizationStatus(userId, status, organizationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['activeSeats'] });
      toast.success(tActionMessages.userStatusUpdatedSuccessfully);
    },
    onError: (error: any) => {
      // Sprint 20, story-093: the caller's own onError (users.tsx) routes this code to the
      // out-of-seats modal instead — the modal is the explanation, no toast needed here.
      if (isNoSeatsAvailableError(error)) return;
      console.error('Update user organization status error:', error);
      const errorMessage =
        error?.response?.errors?.[0]?.message ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        error?.toString() ||
        'Failed to update user status';
      toast.error(errorMessage);
    },
  });
};

export default useUpdateUserOrganizationStatus;
